/**
 * useElementCrossing
 * 
 * A hook that detects when an element crosses a specific point in the viewport.
 * 
 * @param {React.RefObject} ref - The ref of the element to check
 * @param {Object} options - Configuration options
 * @param {number} options.crossPoint - Percentage from top of viewport where we want to detect crossing (0-1)
 * @param {Function} options.onCrossUp - Callback when element crosses the point moving upward
 * @param {Function} options.onCrossDown - Callback when element crosses the point moving downward
 */

import { useState, useEffect } from "react";

const useElementCrossing = (
  ref,
  {
    crossPoint = 0.5, // Default to checking middle of viewport
    onCrossUp = () => {},
    onCrossDown = () => {},
  } = {}
) => {
  const [hasPassedPoint, setHasPassedPoint] = useState(false);
  const [elementPosition, setElementPosition] = useState(null); // 'above' or 'below' the point

  useEffect(() => {
    if (!ref.current) return;

    // Convert crossPoint percentage to pixels from top and bottom
    const viewportHeight = window.innerHeight;
    const pixelsFromTop = crossPoint * viewportHeight;
    const pixelsFromBottom = viewportHeight - pixelsFromTop;

    // Set up margins to create a thin observation band at our cross point
    const rootMargin = `${-pixelsFromTop}px 0px ${-pixelsFromBottom}px 0px`;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Get the direction of movement
          const currentY = entry.boundingClientRect.y;
          const previousY = entry.boundingClientRect.y;
          const isMovingUp = currentY < previousY;

          if (entry.isIntersecting) {
            if (hasPassedPoint) return;
            setHasPassedPoint(true);
            
            // Element is crossing our observation band
            if (isMovingUp) {
              setElementPosition('above');
              if (typeof onCrossUp === 'function') {
                onCrossUp();
              }
              else {
                console.warn("onCrossUp is not a function");
              }
            } else {
              setElementPosition('below');
              if (typeof onCrossDown === 'function') {
                onCrossDown();
              }
              else {
                console.warn("onCrossDown is not a function");
              }
            }
          } else {
            if (hasPassedPoint) {
              setHasPassedPoint(false);
            }
          }
        });
      },
      {
        threshold: [0, 1],
        rootMargin
      }
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref, crossPoint, onCrossUp, onCrossDown, hasPassedPoint]);

  return {
    hasPassedPoint,
    elementPosition
  };
};

export default useElementCrossing; 